import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import ReglementFournisseurController from '../controllers/reglement-fournisseur-controller';

const fmt = (n) => `${Number(n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} TND`;
const MONTHS_FR = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
const WEEKDAYS_FR = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

// Monday-first weekday index (0=Mon … 6=Sun)
const mondayIndex = (jsDay) => (jsDay + 6) % 7;

const ForecastCalendarModal = ({ isOpen, onClose, supplier }) => {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1); // 1-12
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!supplier?.fournisseurId && !supplier?.id) return;
    const id = supplier.fournisseurId ?? supplier.id;
    setLoading(true);
    try {
      const res = await ReglementFournisseurController.getForecast(id, year, month);
      setData(res);
    } catch (e) {
      console.error(e);
      toast.error('Erreur de chargement de la prévision');
    } finally {
      setLoading(false);
    }
  }, [supplier, year, month]);

  useEffect(() => {
    if (isOpen) load();
  }, [isOpen, load]);

  if (!isOpen) return null;

  const supplierName = supplier?.fournisseur?.name || supplier?.name || `#${supplier?.fournisseurId ?? supplier?.id}`;

  const prevMonth = () => {
    if (month === 1) { setMonth(12); setYear((y) => y - 1); }
    else setMonth((m) => m - 1);
  };
  const nextMonth = () => {
    if (month === 12) { setMonth(1); setYear((y) => y + 1); }
    else setMonth((m) => m + 1);
  };

  // Map day-of-month -> forecast entry
  const byDay = {};
  (data?.jours || []).forEach((j) => {
    const d = new Date(j.date);
    byDay[d.getDate()] = j;
  });

  const daysInMonth = new Date(year, month, 0).getDate();
  const firstWeekday = mondayIndex(new Date(year, month - 1, 1).getDay());
  const cells = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const todayKey = `${now.getFullYear()}-${now.getMonth() + 1}`;
  const thisMonthKey = `${year}-${month}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Prévision de paiement</h2>
            <p className="text-sm text-gray-500">{supplierName}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500">
            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
          </button>
        </div>

        {/* Month nav + summary */}
        <div className="px-6 py-4">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-2">
              <button onClick={prevMonth} className="px-3 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 text-sm">←</button>
              <div className="text-lg font-semibold text-gray-900 w-44 text-center">{MONTHS_FR[month - 1]} {year}</div>
              <button onClick={nextMonth} className="px-3 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 text-sm">→</button>
            </div>
            <div className="flex flex-wrap gap-3 text-sm">
              <div className="px-3 py-1.5 rounded-lg bg-emerald-50 text-emerald-700"><span className="font-semibold">Livré : </span>{fmt(data?.totalLivre)}</div>
              <div className="px-3 py-1.5 rounded-lg bg-amber-50 text-amber-700"><span className="font-semibold">Prévision (3 j) : </span>{fmt(data?.totalPrevision)}</div>
              <div className="px-3 py-1.5 rounded-lg bg-gray-100 text-gray-800"><span className="font-semibold">Total : </span>{fmt(data?.totalMois)}</div>
              <div className="px-3 py-1.5 rounded-lg bg-blue-50 text-blue-700"><span className="font-semibold">Taux livraison : </span>{Number(data?.tauxLivraisonMoyen || 0).toFixed(1)}%</div>
            </div>
          </div>

          <div className="flex items-center gap-4 mb-2 text-xs text-gray-500">
            <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-100 border border-emerald-300"></span> Livré (réel)</span>
            <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-100 border border-amber-300"></span> Prévision (3 derniers jours)</span>
          </div>

          {loading ? (
            <div className="py-16 text-center text-gray-500">Chargement…</div>
          ) : (
            <div className="border border-gray-200 rounded-xl overflow-hidden">
              {/* Weekday header */}
              <div className="grid grid-cols-7 bg-gray-50 border-b border-gray-200">
                {WEEKDAYS_FR.map((w) => (
                  <div key={w} className="px-2 py-2 text-center text-xs font-semibold text-gray-600">{w}</div>
                ))}
              </div>
              {/* Day cells */}
              <div className="grid grid-cols-7">
                {cells.map((d, idx) => {
                  if (d === null) return <div key={`e${idx}`} className="min-h-[92px] border-b border-r border-gray-100 bg-gray-50/40" />;
                  const entry = byDay[d];
                  const isForecast = entry?.isPrevision;
                  const hasValue = entry && entry.montantPrevu > 0;
                  const isToday = thisMonthKey === todayKey && d === now.getDate();
                  return (
                    <div
                      key={d}
                      className={`min-h-[92px] border-b border-r border-gray-100 p-2 flex flex-col ${
                        hasValue ? (isForecast ? 'bg-amber-50' : 'bg-emerald-50') : 'bg-white'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className={`text-xs font-semibold ${isToday ? 'bg-[#174540] text-white rounded-full w-5 h-5 flex items-center justify-center' : 'text-gray-500'}`}>{d}</span>
                        {hasValue && (
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${isForecast ? 'bg-amber-200 text-amber-800' : 'bg-emerald-200 text-emerald-800'}`}>
                            {isForecast ? 'prév.' : 'livré'}
                          </span>
                        )}
                      </div>
                      {hasValue && (
                        <div className="mt-auto">
                          <div className={`text-sm font-bold ${isForecast ? 'text-amber-700' : 'text-emerald-700'}`}>{fmt(entry.montantPrevu)}</div>
                          <div className="text-[11px] text-gray-500">{entry.itemsLivresEstimes} article(s){entry.nbCommandes ? ` • ${entry.nbCommandes} cmd` : ''}</div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForecastCalendarModal;
